/* eslint-disable no-param-reassign */
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { Schema } from 'yup';
import React from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import { Member } from 'src/modules/member/models/member';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import { useGlobals } from 'src/shared/contexts/appContext';
import { toast } from 'react-toastify';
import { sendGroupMessage } from 'src/shared/services/groupMessageService';
import { useTranslation } from 'react-i18next';
import { Send } from '@mui/icons-material';
import { Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle } from '@mui/material';
import { DialogCloseButton } from 'src/shared/components/organisms/dialogCloseButton';
import { LoadingButton } from '@mui/lab';
import { getSenderTitle } from 'src/shared/functions/getSenderTitle';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import { ComposeEmailForm } from './composeEmailForm';
import { ComposeEmail } from '../models/composeEmail';
import { SelectionTypeEnum } from '../models/selectionType.enum';

type MailModalProps = {
  modalOpen: boolean;
  members: Member[] | [];
  // onSave: () => void;
  onClose: () => void;
};

export const ComposeEmailModal = (props: MailModalProps) => {
  const { modalOpen, members, onClose } = props;
  const { currentUser } = useGlobals();
  const { t } = useTranslation(['groupMessage', 'common', 'member']);

  const isMounted = useIsMounted();

  const componentId = `dialog-${Date.now().toString(36) + Math.random().toString(36).substring(2)}`;

  const requiredMessage = t('common:validationMessages.required');

  const ComposeEmailSchema: Schema<ComposeEmail> = yup.object().shape({
    recipients: yup.string().trim().optional(),
    selection_id: yup.number().required(requiredMessage),
    subject: yup.string().required(requiredMessage),
    body: yup.string().required(requiredMessage),
    sender: yup.string().email().required(requiredMessage),
    send: yup.boolean().required(),
    circularName: yup.string().optional().nullable(),
    isHTML: yup.boolean().required(),
    senderTitle: yup.string().optional(),
    selectionType: yup.number().optional(),
    speedFormValues: yup.string().optional(),
    speedFormText: yup.string().optional(),
    includeRecipientsThatHasUnsubscribedFromEmail: yup.boolean().optional(),
  });

  const formMethods = useForm<ComposeEmail>({
    resolver: yupResolver(ComposeEmailSchema),
    mode: 'all',
    defaultValues: {
      subject: '',
      sender: currentUser?.email || '',
      body: '',
      send: true,
      circularName: '',
      senderTitle: getSenderTitle(currentUser),
      selectionType: SelectionTypeEnum.Selection,
      includeRecipientsThatHasUnsubscribedFromEmail: false,
      selection_id: 0,
      isHTML: true,
      speedFormText: '',
      speedFormValues: '',
      recipients: members
        .filter((x) => x.email)
        .map((x) => x.email)
        .join(';'),
    },
  });

  const {
    handleSubmit,
    formState: { isSubmitting },
  } = formMethods;

  const isUnsubscribed = members.length === 1 && members[0].newsletterEmailUnsubscribed === true;

  const onHandleClose = React.useCallback(() => {
    onClose();
  }, [onClose]);

  const onSubmit = async (data: ComposeEmail) => {
    ConsoleHelper.log('onSubmit', data);
    // await trigger();
    try {
      data.send = true;
      data.circularName = data.subject;
      data.recipients = members
        .filter((x) => x.email)
        .map((x) => x.email)
        .join(';');
      if (!data.senderTitle) {
        data.senderTitle = data.subject;
      }
      const res = await sendGroupMessage(data);
      if (isMounted()) {
        ConsoleHelper.log('res sendGroupMessage', res);
        if (members.length === 1) {
          toast.success(<>{t('member:memberDetails.emailSendSuccess')}</>);
        } else {
          toast.success(<>{t('groupMessage:create.sendSuccess')}</>);
        }
        onHandleClose();
      }
    } catch (e) {
      if (isMounted()) {
        if (members.length === 1) {
          toast.warning(<>{t('member:memberDetails.emailSendFailed')}</>);
        } else {
          toast.warning(<>{t('groupMessage:create.sendError')}</>);
        }
        ConsoleHelper.error('saveFormDataError:', e);
      }
    }
  };

  const onHandleSave = React.useCallback(() => {
    const membersWithMail = members.filter((x) => x.email);
    if (membersWithMail.length > 0) {
      handleSubmit(onSubmit)();
      // formRef.current?.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
    } else {
      toast.error(<>{t('common:statusMessages.generalError')}</>);
    }
  }, [members]);

  const setDialogHeader = () => {
    const membersWithMail = members.filter((x) => x.email);
    if (membersWithMail.length > 0) {
      return membersWithMail.length > 1
        ? t('groupMessage:create.SendToMembers', { count: membersWithMail.length })
        : t('groupMessage:create.SendToMember', { email: membersWithMail[0]?.email });
    }
    return t('groupMessage:selection.noUserHasEmail');
  };

  return (
    <FormProvider {...formMethods}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Dialog
          onClose={onHandleClose}
          aria-labelledby={`${componentId}-titel`}
          open={modalOpen}
          maxWidth='md'
          fullWidth
          disableEnforceFocus
          sx={{
            minWidth: {
              xs: '90vw',
              md: '400px',
            },
            '& .MuiDialogContent-root': {
              p: 2,
            },
            '& .MuiDialogActions-root': {
              p: 1,
            },
          }}
        >
          <DialogTitle sx={{ m: 0, p: 2 }} id={`${componentId}-titel`}>
            {setDialogHeader()}
            <DialogCloseButton handleClose={onHandleClose} />
          </DialogTitle>
          <DialogContent dividers>
            {isUnsubscribed && (
              <Alert severity='warning' sx={{ mb: 2 }}>
                {t('member:communication.unsubscribedEmailAlert')}
              </Alert>
            )}
            <ComposeEmailForm members={members} disabled={isUnsubscribed} />
          </DialogContent>
          <DialogActions>
            <Box sx={{ p: 1 }}>
              <Button variant='outlined' color='primary' disableElevation onClick={onHandleClose} sx={{ mr: 2 }}>
                {t('common:button.cancel')}
              </Button>
              <LoadingButton
                autoFocus
                loading={isSubmitting}
                variant='contained'
                type='submit'
                disabled={members.filter((x) => x.email).length === 0 || isUnsubscribed}
                onClick={onHandleSave}
                endIcon={<Send aria-hidden fontSize='small' />}
              >
                {t('common:button.send')}
              </LoadingButton>
            </Box>
          </DialogActions>
        </Dialog>
      </form>
    </FormProvider>
  );
};
